import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Save, Search } from "lucide-react";
import { Navigate } from "react-router";
import { useAdminAuth } from "../auth/AdminAuthContext";
import { uiStringsApi } from "../../api/uiStrings";
import { formatApiError } from "../../api/client";
import { flattenLocaleSections } from "../uiStringKeys";
import ru from "../../../locales/ru.json";
import en from "../../../locales/en.json";

type Draft = { ru: string; en: string };
type DirtyLocale = "ru" | "en";

function dirtyId(key: string, locale: DirtyLocale): string {
  return `${key}\0${locale}`;
}

function parseDirtyId(id: string): { key: string; locale: DirtyLocale } | null {
  const i = id.indexOf("\0");
  if (i <= 0) return null;
  const locale = id.slice(i + 1);
  if (locale !== "ru" && locale !== "en") return null;
  return { key: id.slice(0, i), locale };
}

export function AdminUiStringsPage() {
  const { user } = useAdminAuth();
  const defaultsRu = useMemo(() => flattenLocaleSections(ru as Record<string, unknown>), []);
  const defaultsEn = useMemo(() => flattenLocaleSections(en as Record<string, unknown>), []);
  const allKeys = useMemo(
    () => Array.from(new Set([...Object.keys(defaultsRu), ...Object.keys(defaultsEn)])).sort(),
    [defaultsRu, defaultsEn],
  );

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  /** Per-locale dirty flags — never send untouched locale (avoids wiping EN overrides). */
  const [dirty, setDirty] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await uiStringsApi.fetchAdmin();
      const next: Record<string, Draft> = {};
      for (const key of allKeys) {
        next[key] = {
          ru: data.overrides[key]?.ru ?? "",
          en: data.overrides[key]?.en ?? "",
        };
      }
      setDrafts(next);
      setDirty(new Set());
    } catch (err) {
      setError(formatApiError(err, "Не удалось загрузить тексты"));
    } finally {
      setLoading(false);
    }
  }, [allKeys]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirtyKeyCount = useMemo(() => {
    const keys = new Set<string>();
    for (const id of dirty) {
      const parsed = parseDirtyId(id);
      if (parsed) keys.add(parsed.key);
    }
    return keys.size;
  }, [dirty]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allKeys;
    const q = query.trim().toLowerCase();
    return allKeys.filter((k) => {
      const d = drafts[k];
      return (
        k.toLowerCase().includes(q) ||
        (defaultsRu[k] ?? "").toLowerCase().includes(q) ||
        (defaultsEn[k] ?? "").toLowerCase().includes(q) ||
        (d?.ru ?? "").toLowerCase().includes(q) ||
        (d?.en ?? "").toLowerCase().includes(q)
      );
    });
  }, [allKeys, query, drafts, defaultsRu, defaultsEn]);

  if (user?.role !== "super_admin") {
    return <Navigate to="/" replace />;
  }

  const save = async () => {
    const byKey = new Map<string, { key: string; ru?: string; en?: string }>();
    for (const id of dirty) {
      const parsed = parseDirtyId(id);
      if (!parsed) continue;
      const item = byKey.get(parsed.key) ?? { key: parsed.key };
      item[parsed.locale] = drafts[parsed.key]?.[parsed.locale] ?? "";
      byKey.set(parsed.key, item);
    }
    const items = Array.from(byKey.values());
    if (!items.length) return;
    setSaving(true);
    setError("");
    try {
      await uiStringsApi.saveAdmin(items);
      setDirty(new Set());
      await load();
    } catch (err) {
      setError(formatApiError(err, "Не удалось сохранить"));
    } finally {
      setSaving(false);
    }
  };

  const setField = (key: string, locale: DirtyLocale, value: string) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? { ru: "", en: "" }), [locale]: value },
    }));
    setDirty((prev) => new Set(prev).add(dirtyId(key, locale)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-gray-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Загрузка…
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-between">
        <div>
          <h1 className="text-[20px] text-gray-900">Системные тексты</h1>
          <p className="text-sm text-gray-500 mt-1">
            Override RU/EN для chrome mini-app (nav, login, profile…). Пустое поле при сохранении
            этой локали = сброс к дефолту из кода. Неизменённая локаль на сервер не уходит — EN
            overrides супер-админа не затираются при правке только RU.
          </p>
        </div>
        <button
          type="button"
          disabled={saving || dirty.size === 0}
          onClick={() => void save()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-40"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Сохранить ({dirtyKeyCount})
        </button>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск по ключу или тексту…"
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-gray-200 text-sm"
        />
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      <div className="space-y-3">
        {filtered.map((key) => {
          const draft = drafts[key] ?? { ru: "", en: "" };
          const ruDirty = dirty.has(dirtyId(key, "ru"));
          const enDirty = dirty.has(dirtyId(key, "en"));
          return (
            <div
              key={key}
              className={`bg-white border rounded-xl p-3 space-y-2 ${
                ruDirty || enDirty ? "border-amber-300" : "border-gray-200"
              }`}
            >
              <code className="text-[12px] text-indigo-600">{key}</code>
              <div className="grid sm:grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[11px] text-gray-500">
                    RU{ruDirty ? " · изменено" : ""}
                  </span>
                  <input
                    className="mt-0.5 w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm"
                    value={draft.ru}
                    placeholder={defaultsRu[key] || ""}
                    onChange={(e) => setField(key, "ru", e.target.value)}
                  />
                  {!draft.ru && defaultsRu[key] && (
                    <span className="text-[11px] text-gray-400 line-clamp-2 mt-0.5">
                      {defaultsRu[key]}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="text-[11px] text-gray-500">
                    EN{enDirty ? " · изменено" : ""}
                    {draft.en ? " · override" : ""}
                  </span>
                  <input
                    className="mt-0.5 w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm"
                    value={draft.en}
                    placeholder={defaultsEn[key] || ""}
                    onChange={(e) => setField(key, "en", e.target.value)}
                  />
                  {!draft.en && defaultsEn[key] && (
                    <span className="text-[11px] text-gray-400 line-clamp-2 mt-0.5">
                      {defaultsEn[key]}
                    </span>
                  )}
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
